import { default as WJElement, WjElementUtils, event } from '../wje-element/element.js';
import styles from './styles/styles.css?inline';

const DEFAULT_STACK_POSITION = 'top-end';
const DEFAULT_STACK_DEPTH = 3;
const VALID_STACK_POSITIONS = new Set([
    'top-start',
    'top-center',
    'top-end',
    'bottom-start',
    'bottom-center',
    'bottom-end',
]);
const STACKED_SCALE_STEP = 0.04;
const STACKED_MIN_SCALE = 0.88;
const STACKED_MIN_OPACITY = 0.72;
const STACKED_VISIBLE_SLICE = 16;
const STACKED_EXPANDED_MARGIN = '0.5rem';
const STACKED_PADDING = '1rem';
const STACKED_ENTER_OFFSET = 24;

const toggleStackExpanded = (stack, expanded) => {
    if (expanded) {
        stack.dataset.expanded = 'true';
        return;
    }

    delete stack.dataset.expanded;
};

const syncExpandedStackToasts = (stack, expanded) => {
    Array.from(stack.children)
        .filter((child) => child.tagName === 'WJE-TOAST')
        .forEach((toast) => {
            if (expanded) {
                toast.pause?.();
                return;
            }

            toast.resume?.();
        });
};

const setStackExpandedState = (stack, expanded) => {
    let shouldExpand = stack.dataset.stacked === 'true' && expanded;
    let isExpanded = stack.dataset.expanded === 'true';

    if (shouldExpand === isExpanded) {
        return;
    }

    toggleStackExpanded(stack, shouldExpand);
    syncExpandedStackToasts(stack, shouldExpand);
    stack.updateToastLayout?.();
};

const syncStackExpandedState = (stack) => {
    setStackExpandedState(stack, stack.matches(':hover') || stack.matches(':focus-within'));
};

/**
 * `Toast` is a custom web component that represents a toast notification.
 * @summary This element represents a toast notification.
 * @documentation https://elements.webjet.sk/components/toast
 * @status stable
 * @augments {WJElement}
 * @csspart native - The native part
 * @slot - The content of the toast.
 * @slot media - The media of the toast.
 * @attribute {string} headline - Specifies the headline text of the toast. Represents the main title or heading displayed in the toast.
 * @attribute {boolean} open - Indicates whether the toast is currently open (visible). A value of `true` shows the toast, while `false` hides it.
 * @attribute {number} duration - Determines the duration (in milliseconds) for which the toast is displayed. After this time, the toast will automatically close unless it is manually closed.
 * @attribute {boolean} closable - Specifies whether the toast can be manually closed by the user. If `true`, the toast will include a close button or mechanism.
 * @attribute {string} color - Defines the color variant of the toast. Supports values such as `primary`, `complete`, `success`, `warning`, `danger`, `info`, and `contrast`.
 * @attribute {boolean} countdown - Indicates whether a countdown is displayed in the toast. When `true`, a visual countdown timer is shown to indicate the remaining time before the toast closes.
 * @attribute {boolean} stacked - Enables a layered toast stack where the newest toast stays visually on top while older ones shrink behind it.
 * @attribute {string} stack-position - Defines where the toast stack is placed on the screen. Supports `top-start`, `top-center`, `top-end`, `bottom-start`, `bottom-center`, and `bottom-end`.
 * @attribute {number} stack-depth - Defines how many visual levels the collapsed stacked toast can show. Older toasts beyond this limit stay at the last reduced level. Default is `3`.
 * @attribute {string} icon - Adds a leading icon into the `media` slot by icon name.
 * @cssproperty [--wje-toast-stack-width=300px] - Defines the width of the toast stack container. Useful for centered stacked toasts and demo layouts.
 * // @fires wje-toast:after-show - Fired after the toast is shown.
 * // @fires wje-toast:after-hide - Fired after the toast is hidden.
 */

export default class Toast extends WJElement {
    /**
     * Creates an instance of Toast.
     */
    constructor() {
        super();

        this.toastStack = this.createToastStack();
    }

    /**
     * Set headline value of the toast.
     * @param value
     */
    set headline(value) {
        this.setAttribute('headline', value);
    }

    /**
     * Get headline value of the toast.
     * @returns {string}
     */
    get headline() {
        return this.getAttribute('headline');
    }

    /**
     * Set open value of the toast.
     * @param value
     */
    set open(value) {
        this.removeAttribute('open');

        if (WjElementUtils.stringToBoolean(value)) this.setAttribute('open', value);
    }

    /**
     * Get open value of the toast.
     * @returns {boolean}
     */
    get open() {
        return this.hasAttribute('open');
    }

    /**
     * Set duration value of the toast.
     * @param value
     */
    set duration(value) {
        this.setAttribute('duration', value);
    }

    /**
     * Get duration value of the toast.
     * @returns {number}
     */
    get duration() {
        return +this.getAttribute('duration');
    }

    /**
     * Set closable value of the toast.
     * @param value
     */
    set closable(value) {
        this.setAttribute('closable', value || '');
    }

    /**
     * Get closable value of the toast.
     * @returns {boolean}
     */
    get closable() {
        return this.hasAttribute('closable');
    }

    /**
     * Set color value of the toast.
     * @param value
     */
    set color(value) {
        this.setAttribute('color', value);
    }

    /**
     * Get color value of the toast.
     * @returns {string}
     */
    get color() {
        return this.getAttribute('color');
    }

    /**
     * Set countdown value of the toast.
     * @param value
     */
    set countdown(value) {
        if (value) this.setAttribute('countdown', value);
    }

    /**
     * Get countdown value of the toast.
     * @returns {boolean}
     */
    get countdown() {
        return this.hasAttribute('countdown');
    }

    /**
     * Set stacked value of the toast.
     * @param value
     */
    set stacked(value) {
        this.toggleAttribute('stacked', WjElementUtils.stringToBoolean(value));
    }

    /**
     * Get stacked value of the toast.
     * @returns {boolean}
     */
    get stacked() {
        return this.hasAttribute('stacked');
    }

    /**
     * Set stack depth of the toast.
     * @param value
     */
    set stackDepth(value) {
        if (value === null || value === undefined || value === '') {
            this.removeAttribute('stack-depth');
            return;
        }

        this.setAttribute('stack-depth', value);
    }

    /**
     * Get stack depth of the toast.
     * @returns {number}
     */
    get stackDepth() {
        let value = Number.parseInt(this.getAttribute('stack-depth'), 10);

        if (Number.isFinite(value) && value > 0) {
            return value;
        }

        return DEFAULT_STACK_DEPTH;
    }

    /**
     * Set stack position of the toast.
     * @param value
     */
    set stackPosition(value) {
        if (!value) {
            this.removeAttribute('stack-position');
            return;
        }

        this.setAttribute('stack-position', value);
    }

    /**
     * Get stack position of the toast.
     * @returns {string}
     */
    get stackPosition() {
        let value = this.getAttribute('stack-position');

        if (VALID_STACK_POSITIONS.has(value)) {
            return value;
        }

        if (this.getAttribute('position') === 'bottom') {
            return 'bottom-end';
        }

        return DEFAULT_STACK_POSITION;
    }

    /**
     * Set icon value of the toast.
     * @param value
     */
    set icon(value) {
        this.setAttribute('icon', value);
    }

    /**
     * Get icon value of the toast.
     * @returns {string}
     */
    get icon() {
        return this.getAttribute('icon');
    }

    /**
     * Creates a toast stack container.
     * @returns {HTMLDivElement}
     */
    createToastStack() {
        let stack = Object.assign(document.createElement('div'), { className: 'wje-toast-stack' });

        stack.addEventListener('mouseenter', () => setStackExpandedState(stack, true));
        stack.addEventListener('mouseleave', () => requestAnimationFrame(() => syncStackExpandedState(stack)));
        stack.addEventListener('focusin', () => setStackExpandedState(stack, true));
        stack.addEventListener('focusout', () => requestAnimationFrame(() => syncStackExpandedState(stack)));

        this.syncToastStack(stack);

        return stack;
    }

    /**
     * Returns the key of the toast stack.
     * @returns {string}
     */
    getToastStackKey() {
        return `${this.stackPosition}:${this.stacked ? `stacked:${this.stackDepth}` : 'default'}`;
    }

    /**
     * Applies the stack placement directly on the element so demo/app layout CSS cannot override it accidentally.
     * @param {HTMLDivElement} stack
     */
    applyToastStackPlacement(stack = this.toastStack) {
        const positions = {
            'top-start': {
                top: '0',
                bottom: 'auto',
                left: '0',
                right: 'auto',
                transform: 'none',
                margin: '0.5rem',
            },
            'top-center': {
                top: '0',
                bottom: 'auto',
                left: '50%',
                right: 'auto',
                transform: 'translateX(-50%)',
                margin: '0.5rem 0 0',
            },
            'top-end': {
                top: '0',
                bottom: 'auto',
                left: 'auto',
                right: '0',
                transform: 'none',
                margin: '0.5rem',
            },
            'bottom-start': {
                top: 'auto',
                bottom: '0',
                left: '0',
                right: 'auto',
                transform: 'none',
                margin: '0 0.5rem 0.5rem',
            },
            'bottom-center': {
                top: 'auto',
                bottom: '0',
                left: '50%',
                right: 'auto',
                transform: 'translateX(-50%)',
                margin: '0 0 0.5rem',
            },
            'bottom-end': {
                top: 'auto',
                bottom: '0',
                left: 'auto',
                right: '0',
                transform: 'none',
                margin: '0 0.5rem 0.5rem',
            },
        };

        stack.style.removeProperty('inset-inline');
        stack.style.removeProperty('inset-inline-start');
        stack.style.removeProperty('inset-inline-end');
        Object.assign(stack.style, positions[this.stackPosition] || positions[DEFAULT_STACK_POSITION]);
    }

    /**
     * Applies the current toast stack configuration to a stack element.
     * @param {HTMLDivElement} stack
     */
    syncToastStack(stack = this.toastStack) {
        stack.dataset.position = this.stackPosition;
        stack.dataset.stackKey = this.getToastStackKey();
        stack.updateToastLayout = () => this.updateToastStack(stack);
        stack.style.display = 'flex';
        stack.style.alignItems = 'stretch';
        stack.style.width = 'min(var(--wje-toast-stack-width, 300px), calc(100vw - 1rem))';
        stack.style.maxWidth = 'calc(100vw - 1rem)';
        stack.style.maxHeight = 'calc(100vh - 1rem)';
        stack.style.zIndex = '9999';
        this.applyToastStackPlacement(stack);

        if (this.stacked) {
            stack.dataset.stacked = 'true';
            stack.dataset.stackDepth = String(this.stackDepth);
        } else {
            delete stack.dataset.stacked;
            delete stack.dataset.stackDepth;
            delete stack.dataset.expanded;
        }
    }

    /**
     * Clears transient stack styling from a toast.
     * @param {Toast} toast
     */
    clearStackItemStyles(toast) {
        [
            '--wje-toast-stack-scale',
            '--wje-toast-stack-shift',
            '--wje-toast-stack-opacity',
            '--wje-toast-stack-z',
            '--wje-toast-stack-origin',
            '--wje-toast-stack-host-margin-top',
            '--wje-toast-stack-host-margin-bottom',
            'order',
            'position',
            'top',
            'left',
            'right',
            'width',
            'margin',
            'margin-top',
            'margin-bottom',
        ].forEach((property) => toast.style.removeProperty(property));
    }

    /**
     * Measures the rendered height of a toast for stack overlap calculations.
     * @param {Toast} toast
     * @returns {number}
     */
    getToastVisualHeight(toast) {
        let hostHeight = toast.getBoundingClientRect().height;
        let nativeHeight = toast.shadowRoot?.querySelector('.native-toast')?.getBoundingClientRect().height || 0;

        return Math.max(hostHeight, nativeHeight, toast.offsetHeight || 0);
    }

    /**
     * Recomputes the visual order of toasts inside the stack.
     * @param {HTMLDivElement} stack
     */
    updateToastStack(stack = this.toastStack) {
        let toasts = Array.from(stack.children).filter((child) => child.tagName === 'WJE-TOAST');
        let isStacked = stack.dataset.stacked === 'true';
        let isExpanded = stack.dataset.expanded === 'true';
        let isBottomStack = stack.dataset.position?.startsWith('bottom');
        let transformOrigin = stack.dataset.position?.startsWith('bottom') ? 'center bottom' : 'center top';
        let stackHeight = 0;

        stack.dataset.count = String(toasts.length);
        stack.style.gap = 'var(--wje-spacing-x-small)';
        stack.style.flexDirection = 'column';
        stack.style.overflow = isStacked ? (isExpanded ? 'auto' : 'visible') : 'auto';

        if (isStacked) {
            stack.style.paddingTop = isExpanded ? (isBottomStack ? STACKED_PADDING : '0px') : STACKED_PADDING;
            stack.style.paddingBottom = isExpanded ? (isBottomStack ? '0px' : STACKED_PADDING) : STACKED_PADDING;
        } else {
            stack.style.removeProperty('padding-top');
            stack.style.removeProperty('padding-bottom');
        }

        if (!isStacked || isExpanded) {
            stack.style.removeProperty('height');
        }

        toasts.forEach((toast, index) => {
            this.clearStackItemStyles(toast);

            toast.style.setProperty('--wje-toast-stack-z', String(index + 1));

            if (!isStacked) {
                return;
            }

            if (isExpanded) {
                let visualOrder = isBottomStack ? index : toasts.length - index - 1;
                let isVisualFirst = visualOrder === 0;
                let isVisualLast = visualOrder === toasts.length - 1;

                toast.style.setProperty('--wje-toast-stack-scale', '1.000');
                toast.style.setProperty('--wje-toast-stack-shift', '0px');
                toast.style.setProperty('--wje-toast-stack-opacity', '1.000');
                toast.style.setProperty('--wje-toast-stack-origin', transformOrigin);
                toast.style.order = String(visualOrder);
                toast.style.width = '100%';
                toast.style.setProperty(
                    '--wje-toast-stack-host-margin-top',
                    isBottomStack && !isVisualFirst ? STACKED_EXPANDED_MARGIN : '0px'
                );
                toast.style.setProperty(
                    '--wje-toast-stack-host-margin-bottom',
                    !isBottomStack && !isVisualLast ? STACKED_EXPANDED_MARGIN : '0px'
                );
                return;
            }

            let toastHeight = this.getToastVisualHeight(toast);
            let maxDepth = Math.max(0, this.stackDepth - 1);
            let visibleLevels = Math.min(toasts.length, this.stackDepth);
            let depth = Math.min(toasts.length - index - 1, maxDepth);
            let visibleIndex = Math.max(0, index - Math.max(0, toasts.length - this.stackDepth));
            let targetTop = isBottomStack
                ? visibleIndex * STACKED_VISIBLE_SLICE
                : (visibleLevels - 1 - visibleIndex) * STACKED_VISIBLE_SLICE;
            let scale = Math.max(STACKED_MIN_SCALE, 1 - depth * STACKED_SCALE_STEP);
            let opacity = Math.max(STACKED_MIN_OPACITY, 1 - depth * 0.08);

            toast.style.setProperty('--wje-toast-stack-scale', scale.toFixed(3));
            toast.style.setProperty('--wje-toast-stack-shift', '0px');
            toast.style.setProperty('--wje-toast-stack-opacity', opacity.toFixed(3));
            toast.style.setProperty('--wje-toast-stack-origin', transformOrigin);
            toast.style.position = 'absolute';
            toast.style.top = `${targetTop}px`;
            toast.style.left = '0';
            toast.style.right = '0';
            toast.style.width = '100%';
            toast.style.setProperty('--wje-toast-stack-host-margin-top', '0px');
            toast.style.setProperty('--wje-toast-stack-host-margin-bottom', '0px');

            stackHeight = Math.max(stackHeight, targetTop + toastHeight);
        });

        if (isStacked && !isExpanded) {
            stack.style.height = `${stackHeight}px`;
        }
    }

    /**
     * The class name for the component.
     * @type {string}
     */
    className = 'Toast';

    /**
     * Returns the CSS stylesheet for the component.
     * @static
     * @returns {CSSStyleSheet} The CSS stylesheet
     */
    static get cssStyleSheet() {
        return styles;
    }

    /**
     * Setup attributes for the Button element.
     */
    setupAttributes() {
        this.isShadowRoot = 'open';
        this.setAriaState({
            role: 'status',
        });
    }

    /**
     * Draw method for the toast notification.
     * @returns {object} Document fragment
     */
    draw() {
        let fragment = document.createDocumentFragment();

        let native = document.createElement('div');
        native.setAttribute('part', 'native');
        native.classList.add('native-toast');

        let mediaSlot = document.createElement('slot');
        mediaSlot.setAttribute('name', 'media');
        mediaSlot.classList.add('media');
        mediaSlot.addEventListener('slotchange', () => {
            if (WjElementUtils.hasSlotContent(this.context, 'media')) {
                mediaSlot.parentElement.classList.add('has-media');
            } else {
                mediaSlot.parentElement.classList.remove('has-media');
            }
        });

        let content = document.createElement('div');
        content.classList.add('content');
        content.innerHTML = `<div class="headline">${this.headline}</div><div class="message"><slot></slot></div>`;

        let iconX = document.createElement('wje-icon');
        iconX.setAttribute('name', 'x');

        let closeBtn = document.createElement('wje-button');
        closeBtn.setAttribute('fill', 'link');
        closeBtn.setAttribute('color', this.color);
        closeBtn.setAttribute('size', 'small');
        closeBtn.classList.add('close');
        closeBtn.setAttribute('label', 'Close');

        let countdownEl = document.createElement('div');
        countdownEl.classList.add('countdown');

        let countdownBar = document.createElement('div');
        countdownBar.classList.add('countdown-bar');

        closeBtn.appendChild(iconX);
        countdownEl.appendChild(countdownBar);

        if (this.hasAttribute('icon') && this.icon) {
            let icon = document.createElement('wje-icon');
            icon.setAttribute('name', this.icon);
            icon.setAttribute('color', this.color);
            icon.setAttribute('slot', 'media');
            icon.setAttribute('part', 'icon');

            this.appendChild(icon);
        }

        native.appendChild(mediaSlot);
        native.appendChild(content);

        if (this.closable) native.appendChild(closeBtn);

        if (this.hasAttribute('countdown')) native.appendChild(countdownEl);

        fragment.appendChild(native);

        this.closeBtn = closeBtn;
        this.countdownBar = countdownBar;

        return fragment;
    }

    /**
     * After draw method for the toast notification.
     */
    afterDraw() {
        this.closeBtn.addEventListener('wje-button:click', this.hide);
        this.addEventListener('mouseenter', this.pause);
        this.addEventListener('mouseleave', this.resume);

        if (this.hasAttribute('countdown')) {
            const startWidth = '100%';
            const endWidth = '0';

            this.countdownAnimation = this.countdownBar.animate([{ width: startWidth }, { width: endWidth }], {
                duration: this.duration,
                easing: 'linear',
            });
        }

        if (this.duration > 0) {
            this.remainingTime = this.duration;
            this.startTimer();
        }
    }

    /**
     * Before disconnect method
     * This method is called before the element is disconnected from the document.
     */
    beforeDisconnect() {
        this.closeBtn?.removeEventListener('wje-button:click', this.hide);
        this.removeEventListener('wje-toast:after-hide', this.removeChildAndStack);
        this.removeEventListener('mouseenter', this.pause);
        this.removeEventListener('mouseleave', this.resume);

        clearTimeout(this.timeoutID);
    }

    /**
     * Starts the timer.
     * This method sets the `startTime` property to the current time and sets
     * the `timeoutID` property to the ID of the timeout. The method also
     * dispatches the `wje-toast:after-hide` custom event when the timeout
     * expires.
     */
    startTimer() {
        this.startTime = Date.now();
        if (this.timeoutID) {
            clearTimeout(this.timeoutID);
        }
        this.isTimerPaused = false;
        this.timeoutID = window.setTimeout(() => {
            this.hide();
        }, this.remainingTime);
    }

    /**
     * Stops the timer.
     * This method clears the timeout and calculates the remaining time.
     * The method is called when the toast notification is paused.
     */
    stopTimer() {
        if (!this.timeoutID || this.isTimerPaused) {
            return;
        }

        window.clearTimeout(this.timeoutID);
        this.timeoutID = null;
        const elapsedTime = Date.now() - this.startTime;
        this.remainingTime = Math.max(0, this.remainingTime - elapsedTime);
        this.isTimerPaused = true;
    }

    /**
     * Resumes the timer.
     * This method resumes the timer if the remaining time is greater
     * than zero. The method is called when the toast notification is resumed.
     */
    resumeTimer() {
        if (this.isTimerPaused && this.remainingTime > 0) {
            this.startTimer();
        }
    }

    /**
     * Asynchronously shows the toast notification.
     * This method sets the `open` property to `true` and dispatches the
     * `wje-toast:after-show` custom event. If the toast is already open,
     * the method returns `undefined`.
     */
    show = () => {
        if (this.open) {
            return;
        }

        this.open = true;
        event.dispatchCustomEvent(this, 'wje-toast:after-show');
    };

    /**
     * Asynchronously hides the toast notification.
     * This method sets the `open` property to `false` and dispatches the
     * `wje-toast:after-hide` custom event. If the toast is already hidden,
     * the method returns `undefined`.
     */
    hide = () => {
        if (!this.open) {
            return;
        }

        this.open = false;
        event.dispatchCustomEvent(this, 'wje-toast:after-hide');
    };

    /**
     * Pauses the countdown animation and stops the timer.
     */
    pause = () => {
        this.countdownAnimation?.pause();
        this.stopTimer();
    };

    /**
     * Resumes the countdown animation and resumes the timer.
     */
    resume = () => {
        if (this.toastStack?.dataset.expanded === 'true') {
            return;
        }

        this.countdownAnimation?.play();
        this.resumeTimer();
    };

    /**
     * Removes the toast notification and the toast stack.
     *
     * This method removes the toast notification from the toast stack and
     * removes the toast stack from the document body if the toast stack is
     * empty.
     */
    removeChildAndStack() {
        if (this.parentElement === this.toastStack) {
            this.toastStack.removeChild(this);
        }

        this.toastStack.style.setProperty('--wje-toast-stack-layout-duration', '0s');
        this.toastStack.style.setProperty('--wje-toast-stack-visual-duration', '0s');
        this.updateToastStack(this.toastStack);

        requestAnimationFrame(() => {
            this.toastStack.style.removeProperty('--wje-toast-stack-layout-duration');
            this.toastStack.style.removeProperty('--wje-toast-stack-visual-duration');
        });

        if (this.toastStack.querySelector('wje-toast') === null) {
            this.toastStack.remove();
        }
    }

    /**
     * Asynchronously starts the toast notification.
     * This method appends the toast notification to the document body and
     * shows the toast notification. The method returns a promise that
     * resolves when the toast notification is shown.
     * @returns {Promise<unknown>}
     */
    start = () => {
        return new Promise((resolve) => {
            let stack = document.body.querySelector(`.wje-toast-stack[data-stack-key="${this.getToastStackKey()}"]`);
            if (stack) {
                this.toastStack = stack;
            } else {
                this.toastStack = this.createToastStack();
            }

            this.syncToastStack(this.toastStack);

            if (this.toastStack.parentElement === null) {
                document.body.append(this.toastStack);
            }

            this.toastStack.append(this);
            if (this.stacked) {
                this.toastStack.style.setProperty('--wje-toast-stack-layout-duration', '0s');
                this.toastStack.style.setProperty('--wje-toast-stack-visual-duration', '0s');
                this.style.setProperty(
                    '--wje-toast-enter-offset',
                    this.stackPosition.startsWith('bottom') ? `${STACKED_ENTER_OFFSET}px` : `-${STACKED_ENTER_OFFSET}px`
                );
                this.style.visibility = 'hidden';
                this.show();
                this.updateToastStack(this.toastStack);
            } else {
                this.updateToastStack(this.toastStack);
                this.show();
            }

            if (this.toastStack.dataset.expanded === 'true') {
                this.pause();
            }

            requestAnimationFrame(() => {
                this.updateToastStack(this.toastStack);

                if (this.stacked) {
                    this.style.removeProperty('visibility');
                    this.toastStack.style.removeProperty('--wje-toast-stack-layout-duration');
                    this.toastStack.style.removeProperty('--wje-toast-stack-visual-duration');

                    requestAnimationFrame(() => this.style.setProperty('--wje-toast-enter-offset', '0px'));
                }
            });
            this.updateComplete?.then(() => this.updateToastStack(this.toastStack));

            this.addEventListener('wje-toast:after-hide', this.removeChildAndStack);

            resolve(this);
        });
    };
}
